import {
    connect,
    injectReducer,
    injectSaga,
} from '@vfde-sails/core';
import saga from './saga';
import reducer from './reducer';
import * as mapDispatch from './action';
import { mapStateToProps } from './selector';
import {
    mountLoadingSpinner,
    setLoadingSpinnerVisibility,
} from '../../components/loadingSpinner';
import {
    AppId,
    ContentId,
    CustomerCardId,
} from '../../enums/elementId';
import { ErrorType } from './enum';
import { Namespace } from '../../enum';
import { getUpdateFlags } from './update';
import {
    loadInitialAppState,
    saveStateToSession,
} from '../../util/session';
import { mountAccordion } from '../../components/accordion';
import tariffSelectionContainer from '../TariffSelection';
import { setTariffFoonoteContentVisibility } from '../../components/footnote';
import { TrackingUIType } from '@vfde-sails/tracking';
import {
    toggleElementsVisibilityById,
    toggleElementVisibilityById,
} from '@vfde-brix/ws10/core';
import { debugInfo } from '@vfde-sails/utils';
import {
    mountOfferSummaryCard,
    updateOfferSummaryCard,
    updateOfferSummaryCardBusiness,
} from '../../components/offerSummaryCard';
import isCmsConfigValid from '../../util/cmsValidator';
import { mountDiscoveryCard } from '../../components/discoveryCard';
import { getUpdatedDiscoveryCardBusiness } from '../../util/components/discoveryCard.util';
import { mountIconTextBanner } from '../../components/iconTextBanner';

type State = ReturnType<typeof mapStateToProps>;
type Actions = typeof mapDispatch;

const mountApp = (actions: Actions) => {
    if (!isCmsConfigValid()) {
        actions.setError({
            type: ErrorType.CmsConfig,
            error: Error('Invalid CMS config!'),
        });

        debugInfo('CMS Config is invalid!');

        return {
            loadingSpinner: null,
            offerSummaryCard: null,
            discoveryCard: null,
        };
    }

    // Note: Have to mount from code, because the CMS cannot set the grid widths for the component
    mountIconTextBanner(ContentId.IconTextBanner);
    mountAccordion(ContentId.FaqAccordion, TrackingUIType.FAQ);

    return {
        loadingSpinner: mountLoadingSpinner(AppId.Loading),
        offerSummaryCard: mountOfferSummaryCard(AppId.OfferSummaryCard),
        discoveryCard: mountDiscoveryCard(ContentId.DiscoveryCard),
    };
};

function appContainer (state: State, actions: Actions) {
    injectSaga(Namespace.App, saga);
    injectReducer(Namespace.App, reducer);

    const { loadingSpinner, offerSummaryCard, discoveryCard } = mountApp(actions);

    tariffSelectionContainer();

    actions.initStore(loadInitialAppState());

    // TODO: Request CMS to delete the customer info card
    toggleElementVisibilityById(CustomerCardId.Wrapper, false);

    return {
        getDerivedStateFromProps (newState: State, oldState: State) {
            const { isLoading, selectedTariff, customer, selectedTariffId, error } = newState;
            const update = getUpdateFlags(newState, oldState);

            update.loading && setLoadingSpinnerVisibility(loadingSpinner, isLoading);

            if (update.error) {
                const isError = !!error;

                toggleElementVisibilityById(AppId.Error, isError);
                toggleElementsVisibilityById([
                    AppId.App,
                    AppId.DiscoveryCardWrapper,
                    ContentId.FootnoteAccordionWrapper,
                ], !isError);
            }

            if (update.tariffsChanged && selectedTariff && offerSummaryCard) {
                updateOfferSummaryCard(offerSummaryCard, selectedTariff);
                setTariffFoonoteContentVisibility(selectedTariffId);
            }

            if (update.loginStatus) {
                const isNotificationVisible = !!customer && !customer.hasDslContract && customer.hasCableContract;

                toggleElementVisibilityById(AppId.CableCustomerNotification, isNotificationVisible);

                offerSummaryCard && updateOfferSummaryCardBusiness(offerSummaryCard, customer);
                discoveryCard?.update(getUpdatedDiscoveryCardBusiness(discoveryCard, customer));
            }

            saveStateToSession(newState);
        },
    };
}

export default connect(mapStateToProps, mapDispatch)(appContainer);
